import React from 'react';
import { Content, Page } from '@backstage/core-components';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@material-ui/icons/Home';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';


const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '85vh',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  dotGrid: {
    position: 'absolute',
    zIndex: -1,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'radial-gradient(circle, #ccc 1px, transparent 1px)',
    backgroundSize: '30px 30px',
    opacity: 0.2,
  },
  iconWrapper: {
    marginBottom: theme.spacing(3),
    animation: '$bounce 2s infinite',
  },
  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-20px)' },
  },
  '@keyframes spin': {
    to: { strokeDashoffset: -160 },
  },
  errorMessage: {
    fontSize: '2rem',
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  errorDescription: {
    fontSize: '1.2rem',
    maxWidth: '600px',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: theme.spacing(4),
  },
  button: {
    padding: theme.spacing(1, 3),
    borderRadius: '50px',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'box-shadow 0.2s',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': { backgroundColor: theme.palette.secondary.dark },
  },
  outlinedButton: {
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },
}));

const ErrorIcon = () => (
  <svg width="220" height="180" viewBox="0 0 220 180" fill="none" xmlns="http://www.w3.org/2000/svg">
    <style>{`
      @keyframes spin { to { stroke-dashoffset: -239; } }
      @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      @keyframes pulse-ring { 0%,100% { opacity: 0.4; } 50% { opacity: 0.9; } }
      .float-group { animation: float 3s ease-in-out infinite; }
      .pulse-ring { animation: pulse-ring 2s ease-in-out infinite; }
    `}</style>

    {/* Soft background blob */}
    <ellipse cx="110" cy="130" rx="90" ry="18" fill="#e3f2fd" opacity="0.5" />

    <g className="float-group">
      {/* Outer pulse ring */}
      <circle cx="110" cy="82" r="58" stroke="#bbdefb" strokeWidth="1" fill="none" strokeDasharray="4 3" className="pulse-ring" />

      {/* Main circle background */}
      <circle cx="110" cy="82" r="48" fill="#e3f2fd" opacity="0.6" />
      <circle cx="110" cy="82" r="48" stroke="#90caf9" strokeWidth="1.5" fill="none" />

      {/* Spinning arc */}
      <circle
        cx="110" cy="82" r="38"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        stroke="rgba(26,35,50,0.12)"
      />
      <circle
        cx="110" cy="82" r="38"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        stroke="#1565c0"
        strokeDasharray="60 180"
        strokeDashoffset="0"
        style={{ transformOrigin: '110px 82px', animation: 'spin 1.6s linear infinite' }}
      />

      {/* Inner white circle */}
      <circle cx="110" cy="82" r="30" fill="white" opacity="0.9" />

      {/* Exclamation mark */}
      <rect x="107.5" y="64" width="5" height="18" rx="2.5" fill="#1565c0" />
      <circle cx="110" cy="91" r="3" fill="#1565c0" />
    </g>

    {/* Floating broken link dots — left */}
    <circle cx="38" cy="60" r="5" fill="#90caf9" opacity="0.7" />
    <circle cx="52" cy="44" r="3" fill="#bbdefb" opacity="0.6" />
    <circle cx="28" cy="82" r="3" fill="#bbdefb" opacity="0.5" />
    <line x1="38" y1="60" x2="62" y2="72" stroke="#90caf9" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />

    {/* Floating broken link dots — right */}
    <circle cx="182" cy="60" r="5" fill="#90caf9" opacity="0.7" />
    <circle cx="168" cy="44" r="3" fill="#bbdefb" opacity="0.6" />
    <circle cx="192" cy="82" r="3" fill="#bbdefb" opacity="0.5" />
    <line x1="182" y1="60" x2="158" y2="72" stroke="#90caf9" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />

    {/* Small spark dots around */}
    <circle cx="80" cy="28" r="2.5" fill="#1565c0" opacity="0.25" />
    <circle cx="140" cy="24" r="2" fill="#1565c0" opacity="0.2" />
    <circle cx="160" cy="110" r="2" fill="#90caf9" opacity="0.4" />
    <circle cx="58" cy="108" r="2" fill="#90caf9" opacity="0.4" />
  </svg>
);

type Props = {
  plugin?: object;
  error?: Error;
  resetError?: () => void;
};

export const CustomErrorPage = ({ resetError }: Props) => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Content>
        <div className={classes.dotGrid} />
        <div className={classes.root}>
          <div className={classes.iconWrapper}>
            <ErrorIcon />
          </div>

          <Typography variant="h2" className={classes.errorMessage}>
            Something Went Wrong
          </Typography>

          <Typography variant="body1" className={classes.errorDescription}>
            An unexpected error occurred and the page couldn't be loaded.
            <br />
            Try again — if it keeps happening, check back in a little while.
          </Typography>

          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              className={`${classes.button} ${classes.secondaryButton}`}
              component={RouterLink}
              to={window.location.pathname.startsWith('/sts') ? '/sts' : '/marketplace/home'}
              onClick={resetError}
              startIcon={<HomeIcon />}
            >
              {window.location.pathname.startsWith('/sts') ? 'STS' : 'Marketplace'}
            </Button>
            <Button
              variant="outlined"
              className={`${classes.button} ${classes.outlinedButton}`}
              onClick={() => window.history.back()}
              startIcon={<ArrowBackIcon />}
            >
              Go Back
            </Button>
          </div>
        </div>
      </Content>
    </Page>
  );
};
